import React, { FC, HTMLAttributes, MouseEvent, useEffect, useState } from 'react'
import { Box, InputGroup, InputLeftElement, Input, Icon, IconButton, InputRightElement, Spinner } from '@chakra-ui/core'
import { useLazyQuery } from '@apollo/client'
import { searchProductsByNameQuery } from '../../queries/index'
import { productsSearched } from '@engyalo/storefront-events'
import { IProduct, ISearchStats, IConfig } from '@engyalo/storefront-types/lib/plugins/model'
import { isMobileOnly } from 'react-device-detect'
import {
  IProductsPricedPerCollectionVariables,
  ISearchProduct,
  IProductsPricedPerCollectionCollection,
} from '@engyalo/storefront-types/lib/graphql/product'
import { RemoveScroll } from 'react-remove-scroll'
import { getStoreFront_orderConfig as IOrderConfig } from '@engyalo/storefront-types/lib/graphql/storefront'
import { debounce, notEmpty } from '../../lib/utilities'
import { ProductList, ProductListFrom } from '../ProductList'
import { useParams, useHistory, useLocation } from 'react-router'
import { configState, orderConfigState, searchStats } from '../../recoil/atoms'
import { useRecoilValue, useRecoilState } from 'recoil'
import { ILocationSearch, skipIfInPreviewMode } from '../../utils/mode'
import { usePlugin } from '../../Plugin'
import { Hook, IStorefrontSettings } from '@engyalo/storefront-types'
import { useTranslation } from 'react-i18next'
import { isEmpty } from 'lodash'

interface IProps extends HTMLAttributes<HTMLDivElement> {
  placeholder?: string
  status?: boolean
  closeAction: (event: MouseEvent<HTMLButtonElement>) => void
}

export const Searchbar: FC<IProps> = ({ placeholder, status, closeAction }: IProps) => {
  const { t } = useTranslation()
  const [query, setQuery] = React.useState('')
  const history = useHistory()
  const location = useLocation<ILocationSearch>()
  const orderConfig = useRecoilValue<IOrderConfig>(orderConfigState)
  const config = useRecoilValue<IConfig>(configState)
  const [currentSearchStats, setSearchStats] = useRecoilState<ISearchStats>(searchStats)
  const storefrontPlugin = usePlugin(Hook.STOREFRONT)
  const hidePrices = (storefrontPlugin?.props as IStorefrontSettings)?.hidePrices ?? false
  const [products, setProducts] = React.useState<IProduct[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const searchProductLabel = t('search-product')

  if (placeholder == undefined) {
    placeholder = searchProductLabel
  }

  const session = {
    userID: orderConfig.userID || '',
    customer: config.customer,
    sessionID: orderConfig.id || '',
  }

  const [searchProducts, { data }] = useLazyQuery<ISearchProduct, IProductsPricedPerCollectionVariables>(
    searchProductsByNameQuery,
    {
      onError(error) {
        const HTTPNOTFOUND = '404'
        const notFoundError = error.graphQLErrors.find((error) => error.extensions?.extensions?.code === HTTPNOTFOUND)

        if (notFoundError) {
          const newSearchCount = currentSearchStats.searchCount + 1
          skipIfInPreviewMode(() => {
            productsSearched({
              stats: {
                term: query,
                totalResults: 0,
                totalSearches: newSearchCount,
              },
              session,
            })
          })
          setSearchStats({ ...currentSearchStats, searchCount: newSearchCount })
        }
        setIsLoading(false)
      },
      onCompleted(response) {
        if (query) {
          const newSearchCount = currentSearchStats.searchCount + 1
          skipIfInPreviewMode(() => {
            productsSearched({
              stats: {
                term: query,
                totalResults:
                  response.searchProduct?.reduce(
                    (prev: number, collection: IProductsPricedPerCollectionCollection | null) =>
                      prev + (collection?.productsPriced?.length ?? 0),
                    0
                  ) || 0,
                totalSearches: newSearchCount,
              },
              session,
            })
          })
          setSearchStats({ ...currentSearchStats, searchCount: newSearchCount })
          setIsLoading(false)
        }
      },
    }
  )

  const delayedQuery = React.useCallback(
    debounce((q) => searchProducts(q), 500),
    []
  )

  const { customer, order } = useParams()
  const searchInput = React.useRef<HTMLInputElement>(null)

  const onChange = (e: React.FormEvent<HTMLInputElement>): void => {
    const searchText = e.currentTarget.value
    setQuery(searchText)

    delayedQuery({
      variables: {
        name: customer,
        configId: order,
        product: searchText,
      },
    })
  }

  React.useEffect(() => {
    if (query === '') {
      setProducts([])
    } else {
      const searchResult: IProduct[] = []
      data?.searchProduct?.forEach((collection: IProductsPricedPerCollectionCollection | null) => {
        if (collection && collection.productsPriced) {
          const arr: IProduct[] = collection.productsPriced.filter(notEmpty).map((product) => ({
            ...product,
            enabledPrice: !!collection?.enabledPrice,
            price: hidePrices ? 0 : product.price,
          }))
          searchResult.push(...arr)
        }
      })
      setProducts(searchResult)
    }
  }, [data, query])

  React.useEffect(() => {
    if (status && searchInput.current) {
      searchInput.current.focus()
    }
  }, [status])

  useEffect(() => {
    if (query !== '') {
      setIsLoading(true)
    } else {
      setIsLoading(false)
    }
  }, [query])

  return (
    <Box w="100%" maxW="42.5rem" zIndex={100} height="100%" flexDir="column" position="absolute" d="flex">
      <Box top={0} flex="0 0 auto" position="sticky">
        <InputGroup borderBottom="1px solid" borderColor="primary.500" top={0} flex="0 0 auto" w="100%" maxW="42.5rem">
          <InputLeftElement h="4rem" paddingX="1.6rem">
            <Icon name="search" size="14px" />
          </InputLeftElement>
          <Input
            ref={searchInput}
            border="none"
            h="4rem"
            paddingLeft="4rem"
            type="text"
            value={query}
            onChange={onChange}
            placeholder={placeholder}
          />
          <InputRightElement height="100%">
            <IconButton
              width="3.2rem"
              height="4rem"
              background="transparent"
              borderColor="transparent"
              aria-label="cerrar"
              icon="close"
              onClick={(event: React.MouseEvent<HTMLButtonElement>): void => {
                const search = new URLSearchParams(location.search)
                search.delete('q')
                history.push({ search: search.toString() })
                closeAction(event)
              }}
            />
          </InputRightElement>
        </InputGroup>
      </Box>

      <RemoveScroll forwardProps allowPinchZoom={true}>
        {isLoading ? (
          <Box
            backgroundColor="white"
            zIndex={3}
            flex="1"
            display={'flex'}
            w="100%"
            height="100%"
            maxH={isMobileOnly ? '100%' : '68rem'}
            justifyContent={'center'}
            alignItems={'center'}
          >
            <Spinner id={'spinner'} color="orange.500" thickness="4px" speed="0.65s" emptyColor="gray.200" size="xl" />
          </Box>
        ) : (
          <Box
            backgroundColor="white"
            zIndex={3}
            flex="1"
            w="100%"
            maxW="42.5rem"
            overflowY="scroll"
            height="100%"
            maxH={isMobileOnly ? '100%' : '68rem'}
          >
            {products.length > 0 ? (
              <ProductList products={products} from={ProductListFrom.SEARCH} showPrice={!hidePrices}/>
            ) : (
              !isEmpty(query) && (
                <Box
                  backgroundColor="white"
                  zIndex={3}
                  flex="1"
                  display={'flex'}
                  w="100%"
                  height="100%"
                  maxH={isMobileOnly ? '100%' : '68rem'}
                  justifyContent={'center'}
                  alignItems={'center'}
                >
                  <div>{t("empty_list")}</div>
                </Box>
              )
            )}
            <Box h={75} />
          </Box>
        )}
      </RemoveScroll>
    </Box>
  )
}
